/**
 * Phone number entry for the call panel: the country picker's list, and pure helpers that keep
 * the typed number as national digits, validate and format it per country, and build E.164.
 */

/** A rule on the national digits, checked once at least `from` digits are typed. */
export interface PhoneRule {
  from: number;
  pattern: RegExp;
  message: string;
}

export interface PhoneCountry {
  iso: string;
  name: string;
  /** Dial code without "+". */
  dial: string;
  /** National significant number length (no trunk 0). */
  length: number;
  /** Display mask, "#" = one digit. */
  mask: string;
  rules: PhoneRule[];
}

const NO_TRUNK_ZERO: PhoneRule = { from: 1, pattern: /^[1-9]/, message: "Leave out the leading 0." };

/** NANP (US, Canada): area code and exchange both start 2–9. */
const NANP_RULES: PhoneRule[] = [
  { from: 1, pattern: /^[2-9]/, message: "The area code can't start with 0 or 1." },
  { from: 4, pattern: /^\d{3}[2-9]/, message: "The local number can't start with 0 or 1." },
];

/**
 * Only the countries the call service dials: `AllowedPhoneCountryCodes` in the API's appsettings
 * (default "1", "91"; secret ChimeAllowedPhoneCountryCodes). Add a country there before adding it here.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = [
  { iso: "US", name: "United States", dial: "1", length: 10, mask: "(###) ###-####", rules: NANP_RULES },
  { iso: "CA", name: "Canada", dial: "1", length: 10, mask: "(###) ###-####", rules: NANP_RULES },
  { iso: "IN", name: "India", dial: "91", length: 10, mask: "##### #####", rules: [NO_TRUNK_ZERO] },
];

export const DEFAULT_COUNTRY: PhoneCountry = PHONE_COUNTRIES[0];

export function countryByIso(iso: string): PhoneCountry {
  return PHONE_COUNTRIES.find((c) => c.iso === iso) ?? DEFAULT_COUNTRY;
}

/**
 * The national digits in a typed or pasted value: drops a leading dial code ("+1 415…", "1415…")
 * or trunk 0 ("07700…") when the value is longer than a national number, and caps the length.
 */
export function nationalDigits(country: PhoneCountry, raw: string): string {
  let digits = raw.replace(/\D/g, "");
  const international = raw.trim().startsWith("+");
  if ((international || digits.length > country.length) && digits.startsWith(country.dial)) {
    digits = digits.slice(country.dial.length);
  }
  if (digits.length > country.length && digits.startsWith("0")) digits = digits.slice(1);
  return digits.slice(0, country.length);
}

/** Applies the mask as far as the digits go, so "415" reads "(415" and a trailing separator never sticks. */
export function formatNational(country: PhoneCountry, digits: string): string {
  let out = "";
  let i = 0;
  for (const ch of country.mask) {
    if (i >= digits.length) break;
    out += ch === "#" ? digits.charAt(i++) : ch;
  }
  return out;
}

export function formatInternational(country: PhoneCountry, digits: string): string {
  return `+${country.dial} ${formatNational(country, digits)}`;
}

/** The first broken rule for what is typed so far ("" when none). Shown while typing. */
export function ruleProblem(country: PhoneCountry, digits: string): string {
  const broken = country.rules.find((r) => digits.length >= r.from && !r.pattern.test(digits));
  return broken ? broken.message : "";
}

/** Everything wrong with the number, including being incomplete ("" when it can be dialled). */
export function phoneProblem(country: PhoneCountry, digits: string): string {
  if (!digits) return "Enter a phone number.";
  const rule = ruleProblem(country, digits);
  if (rule) return rule;
  if (digits.length < country.length) return `${country.name} numbers have ${country.length} digits after +${country.dial}.`;
  return "";
}

export function isValidPhone(country: PhoneCountry, digits: string): boolean {
  return !phoneProblem(country, digits);
}

export function toE164(country: PhoneCountry, digits: string): string {
  return `+${country.dial}${digits}`;
}

/** The mask with zeros, as the input's placeholder. */
export function placeholderFor(country: PhoneCountry): string {
  return country.mask.replace(/#/g, "0");
}

/** Splits an E.164 value into a picker country and national digits; anything else lands on the default country. */
export function parsePhoneNumber(value: string): { country: PhoneCountry; digits: string } {
  const all = value.replace(/\D/g, "");
  if (value.trim().startsWith("+")) {
    const match = PHONE_COUNTRIES.find((c) => all.startsWith(c.dial) && all.length - c.dial.length === c.length);
    if (match) return { country: match, digits: all.slice(match.dial.length) };
  }
  return { country: DEFAULT_COUNTRY, digits: nationalDigits(DEFAULT_COUNTRY, value) };
}
